<template>
  <v-container pt-0>
    <div class="mt-6 mb-3 page-header">HawkSoft Text Messaging</div>

    <SequentialStep :completed="currentStep > 1">
      <TextingConfirmCancel
        :feature-on="textingChargeFeatureSwitchOn"
        @input="$event ? currentStep = 2 : currentStep = 1"
      />
    </SequentialStep>

    <v-divider class="my-3" />

    <SequentialStep
      :locked="currentStep < 2"
      :completed="currentStep > 2"
    >
      <div class="headline blue--text mb-4">2. Get Text Message History and Attachments</div>

      <div>
        Before cancelling HawkSoft Text Messaging, you can download your text message history and attachments from HawkSoft. If you would like a copy of your agency text message history and attachment data, please select “Request texting message history and attachments.” We will package all your data and provide a SQLite file to download. There are many free SQLite readers you can use to view the file (like <a target="_blank" href="https://sqlitestudio.pl/">SQLiteStudio</a>).
      </div>
      <div class="mt-2">
        Once you have successfully downloaded your data, please check “I have successfully received my text message history and attachments”.
      </div>

      <v-row dense class="mt-4">
        <v-col class="col-12">
          <v-btn
            :disabled="currentStep < 2 || disableExportButton || downloadInProgress"
            color="success"
            class="white--text"
            style="width: 26rem;"
            @click="startExport"
          >
            Request Text Message History and Attachments</v-btn>
        </v-col>
        <v-col v-if="currentStep >= 2">
          <TextingCancelStatus
            :value="exportJobState"
            :loading="loading"
            :disabled="currentStep < 2"
            class="cols-auto"
          />
        </v-col>
      </v-row>

      <v-row no-gutters class="mb-4">
        <div class="caption">
          Note: This process can take some time depending on how much texting data is stored for the agency.
        </div>
      </v-row>

      <v-row dense>
        <v-col class="col-auto">
          <v-btn
            style="width: 26rem;"
            :disabled="currentStep < 2 || exportJobState < 4 || loading || downloadInProgress"
            color="primary"
            @click="downloadExport"
          >
            Download Text Message History and Attachments
          </v-btn>
          <div v-if="downloadStarted" class="mt-2">
            <DownloadProgress :downloadCurrent="downloadProgress" :downloadTotal="downloadTotal"/>
          </div>
        </v-col>
        <v-col v-if="currentStep >= 2 && exportJobState === 4 && !loading">
          <div v-if="fileName" class="caption">
            File Name: {{ fileName }}
          </div>
          <div v-if="calculatedFileDate" class="caption">
            File Date: {{ calculatedFileDate }}
          </div>
        </v-col>
      </v-row>

      <v-row
        v-if="currentStep >= 2 && exportJobState === 4 && !loading && showOldFileWarning"
        no-gutters
      >
        <div class="caption red--text">
          Warning: This data file is over an hour old, and may not contain the latest texting data. To ensure no data is lost, consider requesting a new texting data file.
        </div>
      </v-row>

      <v-row>
        <v-col>
          Confirm File Downloaded:
          <v-checkbox
            v-model="confirmedStep2Download"
            :disabled="skipStep2 || currentStep < 2 || exportJobState !== 4 || !fileDownloaded"
            label="I have successfully received my text message history and attachments"
            class="mt-0"
            hide-details
            @change="$event ? currentStep = 3 : currentStep = 2"
          />
        </v-col>
      </v-row>
      <v-row>
        <v-col>
          If your agency does not want to get a copy of text message history and attachments, please check the box below. Please note, that if you choose not to get your texting data now, you will not be able to get it in the future, as we delete all texting data after cancellation.
          <v-checkbox
            v-model="skipStep2"
            :disabled="confirmedStep2Download || currentStep < 2"
            label="I do not want a copy of my text message history or attachments"
            class="mt-0"
            hide-details
            @change="$event ? currentStep = 3 : currentStep = 2"
          />
        </v-col>
      </v-row>
    </SequentialStep>

    <v-divider class="my-3" />

    <SequentialStep :locked="currentStep < 3">
      <div class="headline blue--text">3. Cancel HawkSoft Text Messaging</div>
      <v-row no-gutters>
        <v-flex xs12 class="my-2">
          When you cancel HawkSoft Text Messaging, HawkSoft will release your phone number and delete all texting data stored on our servers. Releasing your number makes it available again in the pool of numbers to use. Once released, we will not be able to get that number back. If you enable HawkSoft Text Messaging again in the future, you will receive a new texting number.
        </v-flex>
        <v-row no-gutters class="my-4 mx-4">
          <v-btn
            :disabled="currentStep < 3 || loading"
            color="red"
            class="white--text"
            @click="showDialogLastConfirmation = true"
          >
            Cancel HawkSoft Text Messaging
          </v-btn>
        </v-row>
        <v-row
          v-if="textingChargeFeatureSwitchOn"
          no-gutters
        >
          Once cancelled, the fee for HawkSoft Text Messaging will be removed from your next HawkSoft invoice.
        </v-row>
      </v-row>
    </SequentialStep>

    <v-row class="mt-6">
      <v-btn @click="$router.push('/marketplace');">Return To Marketplace</v-btn>
    </v-row>

    <v-snackbar
      v-model="snackbar.show"
      :timeout="3000"
      :color="snackbar.color"
    >
      {{ snackbar.message }}
    </v-snackbar>

    <DialogConfirmCancel
      v-model="showDialogLastConfirmation"
      :old-file-warning="showOldFileWarning"
      @confirmed="cancelTexting()"
    />

  </v-container>
</template>

<style lang="scss" scoped>
  .v-label {
    font-size: 14px !important;
  }
  .page-header {
    color: #A5814b;
    font-weight: bold;
    font-size: 1.3rem;
  }
</style>

<script>
import productIdMappingMixin from '@/mixins/productIdMappingMixin';
import { mapActions, mapGetters, mapState } from 'vuex';

import Moment from 'moment';
import TextingConfirmCancel from './TextingConfirmCancel.vue';
import DownloadProgress from './DownloadProgress.vue';
import TextingCancelStatus from './TextingCancelStatus.vue';
import SequentialStep from './SequentialStep.vue';
import DialogConfirmCancel from './DialogConfirmCancel.vue';

export default {
  name: 'TextingCancel',
  components: {
    DownloadProgress,
    SequentialStep,
    TextingConfirmCancel,
    TextingCancelStatus,
    DialogConfirmCancel
  },
  mixins: [productIdMappingMixin],
  data: () => ({
    loading: false,
    currentStep: 1,
    confirmedStep2Download: false,
    skipStep2: false,
    fileDownloaded: false,
    snackbar: {
      // For errors/notifications
      show: false,
      color: 'error',
      message: ''
    },
    showDialogLastConfirmation: false,
    showOldFileWarning: false,
    disableExportButton: false,
    // ## Job States
    // NoJob = 0,
    // Created = 1,
    // Processing = 2,
    // Error = 3,
    // FileExists = 4
    exportJobState: 0,
    fileName: null,
    fileDate: null,

    // Job-Check Timer Vars
    checkJob: null,
    checkInterval: 2000,
    checkTimeout: 1000 * 60 * 15, // 15 mins
    checkJobRunning: false,
    checkStartTS: null,

    // Download Started
    downloadStarted: false,
    downloadProgress: 0,
    downloadTotal: 0
  }),
  computed: {
    ...mapState([
      'agency',
      'featureSwitches'
    ]),
    ...mapGetters([
      'getProductByProductId'
    ]),
    calculatedFileDate () {
      if (this.fileDate == null) {
        return null;
      }
      return new Moment(this.fileDate).format('ddd, MMM D YYYY, h:mm:ss a');
    },
    textingChargeFeatureSwitchOn () {
      return (
        this.featureSwitches.TextingProductStatusToggle != null &&
        this.featureSwitches.TextingProductStatusToggle
      );
    },
    downloadInProgress () {
      return this.downloadStarted && !this.fileDownloaded;
    }
  },
  async mounted () {},
  async created () {
    let p1 = this.getTextingCancelStatus();
    let p2 = this.getFeatureSwitches();
    await Promise.all([p1, p2]);
  },
  beforeDestroy () {
    if (this.checkJob) {
      clearInterval(this.checkJob); // Clears any running job
    }
  },
  methods: {
    ...mapActions([
      'newSetProductSubscription',
      'getFeatureSwitches'
    ]),
    setError (msg) {
      this.snackbar.color = 'error';
      this.snackbar.message = msg;
      this.snackbar.show = true;
    },
    setSuccess (msg) {
      this.snackbar.color = 'success';
      this.snackbar.message = msg;
      this.snackbar.show = true;
    },
    async cancelTexting () {
      var product = this.getProductByProductId(this.getProductId('texting'));
      if (!product) {
        this.setError('Something went wrong. Please try again later.');
        return;
      }

      this.loading = true;
      try {
        // Only send an email if the feature switch is on.
        let sendEmail = this.textingChargeFeatureSwitchOn;
        var response = await this.newSetProductSubscription({
          productId: product.id,
          subscription: false,
          sendEmail: sendEmail
        });
        if (response.code !== 0) {
          this.setError(`Error - Unable to cancel subscription. Message: ${response.message}`);
          this.loading = false;
          return;
        }

        // Success
        this.$router.push('/product/texting/cancelled');
      } catch (ex) {
        this.setError('Something went wrong. Please try again later.');
      } finally {
        this.loading = false;
      }
    },
    async getTextingCancelStatus () {
      this.loading = true;
      let response = await this.$api.getTextingExportStatus();

      if (response.status === 200) {
        var rsData = response.data;
        if (rsData.code !== 0) {
          this.setError(`Error Checking Texting Status. Message: ${rsData.message}`);
          return;
        }

        var status = rsData.data[0];

        this.exportJobState = status.state;
        if (this.exportJobState === 4 && status.meta != null) {
          this.fileName = status.meta.filename;
          this.fileDate = status.meta.lastModified;
          if (Moment(Date.now()).isAfter(Moment(this.fileDate).add(1, 'hours'))) {
            // File considered old. Show warning to user
            this.showOldFileWarning = true;
          }
        }
      }

      this.loading = false;
    },
    async startExport () {
      this.loading = true;

      // eslint-disable-next-line no-unused-vars
      let response = await this.$api.triggerTextingDataExport();
      if (response.status !== 200) {
        this.setError('Error - Unable to trigger export');
        return;
      } else {
        var data = response.data;
        if (data.code !== 0) {
          this.setError(`Error - Texting Export Failed. Message: ${data.message}`);
          this.loading = false;
          return;
        }

        this.disableExportButton = true;
        // clear any previously created file
        this.exportJobState = 1;
        this.showOldFileWarning = false;
        this.fileName = null;
        this.fileDate = null;

        // Start job to check progress
        this.checkStartTS = Date.now();
        this.checkJob = setInterval(this.intervalProgressCheck, this.checkInterval);
      }
      this.loading = false;
    },
    handleDownloadProgress (loaded, total) {
      this.downloadProgress = loaded;
      this.downloadTotal = total;
    },
    async downloadExport () {
      this.downloadStarted = true;
      this.fileDownloaded = false;
      this.downloadProgress = 0;
      this.downloadTotal = 0;

      await this.$api.downloadTextingExportFile(this.handleDownloadProgress);
      this.fileDownloaded = true;
    },
    async intervalProgressCheck () {
      if (this.checkJobRunning) { // Don't have more than one check at a time running
        return;
      }
      // Check for timeout
      if (this.checkStartTS + this.checkTimeout < Date.now()) { // job running too long
        this.exportJobState = 3; // sets to an errored state
        clearInterval(this.checkJob); // clear timed job
        return;
      }
      this.checkJobRunning = true;
      await this.getTextingCancelStatus();
      if (this.exportJobState >= 3) { // job finished
        clearInterval(this.checkJob); // clear timed job
        return;
      }
      this.checkJobRunning = false;
    }

  }
};
</script>
